import eventPlanning from "@/data/event-planning.json";
import catchEventConceptAsset from "@/assets/eventsverige-catch-event-konceptbild.png.asset.json";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Sun, Moon, Sparkles, Check } from "lucide-react";
import { ConceptGrid } from "./ConceptGrid";

export function ConceptLanding({
  segment,
  campaign = false,
}: {
  segment?: string;
  campaign?: boolean;
}) {
  const title = campaign
    ? "Gör vintern till något att samlas kring."
    : segment === "massa"
      ? "Ett mässbesök som blir ett möte."
      : segment === "foretag"
        ? "När kollegorna får tid att mötas."
        : "Så blir er lokal en plats att mötas på.";
  return (
    <div className="concept-site">
      <section className="experience-hero">
        <div className="experience-wrap hero-grid">
          <div className="hero-copy">
            <p className="eyebrow">
              {campaign
                ? "Vinterevent · Mässor · Kickoff"
                : "Företagsevent & mässor / Event Sverige"}
            </p>
            <h1>{title}</h1>
            <p className="hero-intro">
              {segment === "massa"
                ? "En aktivitet väcker nyfikenheten. En värd gör det lätt att börja. Tillsammans skapar vi en tydlig väg från spel till samtal med ert team."
                : "Ni väljer känslan. Vi förbereder spelen, riggar stämningsljuset och leder det program som ingår. Från första välkomnandet till den gemensamma höjdpunkten."}
            </p>
            <div className="hero-actions">
              <Link to="/koncept" className="experience-button">
                Hitta ert koncept <ArrowRight size={18} />
              </Link>
              <Link to="/forfragan" className="experience-button outline">
                Planera med oss
              </Link>
            </div>
            <div className="hero-notes">
              <span>
                <Sun size={16} /> På dagen
              </span>
              <span>
                <Moon size={16} /> På kvällen
              </span>
              <span>
                <Sparkles size={16} /> Ljus ingår
              </span>
            </div>
          </div>
          <div className="hero-photo hero-photo-flow">
            <div className="warm-glow" />
            <div className="hero-photo-frame">
              <img
                src={catchEventConceptAsset.url}
                alt="Konceptillustration av tre personer som spelar Catch the Light i en eventmiljö med stämningsljus"
                width="1536"
                height="1024"
                fetchPriority="high"
              />
            </div>
            <div className="photo-note">
              <span>Konceptillustration</span>
              <strong>Så kan ert event kännas</strong>
            </div>
          </div>
        </div>
      </section>
      <section className="experience-wrap experience-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Välj upplevelsen</p>
            <h2>
              {segment === "massa"
                ? "Från nyfikenhet till dialog."
                : "Ett upplägg att längta till."}
            </h2>
          </div>
          <p>
            Mingel, lagkamp eller egen arkadfest. På mässan formar vi aktiviteten kring mötet med
            era besökare.
          </p>
        </div>
        <ConceptGrid segment={segment} limit={6} />
        <Link to="/koncept" className="text-link mt-8">
          Se alla koncept <ArrowRight size={16} />
        </Link>
      </section>
      <section className="season-section">
        <div className="experience-wrap season-grid">
          <div>
            <p className="eyebrow">September–april / Och juni</p>
            <h2>
              Varm stämning.
              <br />
              Hela året.
            </h2>
            <p>
              Inomhus från september till april skapar vi en välkomnande miljö. I juni passar samma
              koncept inför semestern, i er lokal eller på konferensanläggningen.
            </p>
          </div>
          <div className="season-options">
            <div>
              <Sun />
              <h3>En del av dagen</h3>
              <p>
                En gemensam start efter konferensen, en paus som samlar gruppen eller en
                sommaravslutning.
              </p>
            </div>
            <div>
              <Moon />
              <h3>En kväll tillsammans</h3>
              <p>
                Stämningsljus får större plats när lokalens godkända grundbelysning dämpas.
                Ljudnivån lämnar utrymme för samtal.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="experience-wrap experience-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Från idé till genomförande</p>
            <h2>
              Ni kan vara med.
              <br />
              Vi håller ihop aktiviteterna.
            </h2>
          </div>
          <p>
            Utrustning, programtid, bemanning och ansvar framgår av konceptet och bekräftas i
            offerten.
          </p>
        </div>
        <div className="delivery-steps">
          {[
            {
              n: "01",
              title: "Berätta om er dag",
              text: "Datum, plats, deltagarantal och önskad känsla. Vi börjar med era förutsättningar.",
            },
            {
              n: "02",
              title: "Välj ett tydligt upplägg",
              text: "Vi stämmer av lokal, tillträde och önskemål. Ni får ett samlat offertunderlag före bokning.",
            },
            {
              n: "03",
              title: "Möt era gäster",
              text: "Vi riggar aktiviteter och stämningsljus, leder det överenskomna programmet och tar ned efteråt.",
            },
          ].map((s) => (
            <div key={s.n}>
              <span>{s.n}</span>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="experience-wrap">
        <div className="planning-panel">
          <div>
            <p className="eyebrow">Börja med idéerna</p>
            <h2>{eventPlanning.title}</h2>
            {eventPlanning.body.map((text) => (
              <p key={text}>{text}</p>
            ))}
            <p>{eventPlanning.scopeNote}</p>
          </div>
          <div>
            <p>
              <Check size={18} /> Stämningsljus i alla eventpaket
            </p>
            <p>
              <Check size={18} /> Tydligt innehåll och ansvar
            </p>
            <p>
              <Check size={18} /> Samlad offert före bokning
            </p>
            <Link to="/forfragan" className="experience-button">
              Skicka era önskemål <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
